import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { LvTextItem } from './lvPriceMatch';

/**
 * Textebene eines PDF samt Seitenmassen — die Datenquelle des LV-Markiermodus.
 *
 * Der Worker kommt ueber den Vite-`?url`-Import aus dem Bundle und NICHT von
 * unpkg: die CDN-Variante ist auf eine feste Version verdrahtet, waehrend
 * package.json `^4.0.379` erlaubt. Ein `npm update` zieht die lokale Bibliothek
 * hoch, der Worker bleibt stehen, und pdf.js bricht mit "API version does not
 * match Worker version" ab — offline ohnehin.
 */
GlobalWorkerOptions.workerSrc = pdfWorkerUrl as string;

export interface PdfPageText {
    items: LvTextItem[];
    /** Seitenmasse bei Skalierung 1 (pt). */
    width: number;
    height: number;
}

/**
 * Liest alle Seiten aus. Die Koordinaten sind PDF-User-Space (Ursprung unten
 * links) — dasselbe System, in dem pdf-lib zeichnet und in dem die reine
 * Markierlogik rechnet.
 */
export const extractPdfPages = async (bytes: Uint8Array): Promise<PdfPageText[]> => {
    // pdfjs transferiert den Buffer in den Worker (detached) — deshalb Kopie.
    // Ohne sie ist dasselbe Uint8Array beim anschliessenden Upload leer.
    const doc = await getDocument({ data: bytes.slice() }).promise;
    try {
        const pages: PdfPageText[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
            const page = await doc.getPage(i);
            const viewport = page.getViewport({ scale: 1 });
            const content = await page.getTextContent();
            pages.push({
                width: viewport.width,
                height: viewport.height,
                items: (content.items as Array<{ str?: string; transform: number[]; width: number; height?: number }>)
                    .filter((it) => typeof it.str === 'string' && it.str !== '')
                    .map((it) => ({
                        str: it.str as string,
                        x: it.transform[4],
                        y: it.transform[5],
                        width: it.width,
                        // transform[3] ist die vertikale Textskalierung und
                        // entspricht der Schriftgroesse in pt; height als Fallback.
                        fontSize: Math.abs(it.transform[3]) || it.height || undefined,
                    })),
            });
        }
        return pages;
    } finally {
        await doc.destroy();
    }
};

/**
 * Hat das Dokument ueberhaupt eine Textebene? Ein reiner Scan liefert leere
 * Item-Listen; dort ist der Markiermodus sinnlos und der Nutzer muss beim
 * KI-Pfad bleiben.
 */
export const hasTextLayer = (pages: PdfPageText[]): boolean =>
    pages.some((p) => p.items.some((it) => it.str.trim() !== ''));
